import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestStatistic } from 'api/sfms/dashboard/rawMaterial/price';
import Flex from 'components/common/Flex';
import Select from 'components/common-sfims/form/Select';
import List from 'components/common-sfims/List';
import { array2ValueAndText } from 'function/convert';
import { onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import formatNumber from 'utils/format/number';

/**
 * 대시보드 > 메인 대시보드 - 원료 단가
 */
const RawMaterial = ({ date }) => {
  /* variable */
  const { fatoryCodeSeq } = getLoginInform();

  /* router */
  const history = useHistory();

  /* recoil */
  const {
    plant: { hasReads: hasReadPlants }
  } = usePlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 목록 끝 번호
  const [end, setEnd] = useState(5);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 키 목록
  const [keys, setKeys] = useState([]);
  // 메시지
  const [message, setMessage] = useState({});
  // 원료 단가 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({ factoryCodeSeq: fatoryCodeSeq });
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* watch */
  useEffect(() => {
    requestList();
  }, [date, params]);

  /* method */
  // 원료 단가 목록 조회 요청
  const requestList = async () => {
    setDatas([]);
    setEnd(5);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        data: { list = [], keyList = [] } = {},
        code,
        message
      } = await requestStatistic({
        ...params,
        date
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(list);
      setKeys(keyList);
      setTotal(list.length);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* view */
  return (
    <Card className="mb-3">
      <Card.Body className="p-0">
        <div id="scroll" className="scrollbar" style={{ maxHeight: '275px' }}>
          <InfiniteScroll
            hasMore={end < total}
            dataLength={datas.slice(0, end).length}
            scrollableTarget="scroll"
            next={() => setEnd(end + 5)}
          >
            <List
              className={{
                table: 'mb-0',
                thead: 'bg-100 text-900 border-200'
              }}
              columns={[
                {
                  text: (
                    <Flex justifyContent="center" alignItems="center">
                      <Select
                        datas={array2ValueAndText(
                          hasReadPlants,
                          'seq',
                          'pltCode'
                        )}
                        placeholder="selectFactory"
                        value={params.factoryCodeSeq}
                        className="me-1"
                        name="factoryCodeSeq"
                        style={{ width: '150px' }}
                        onChange={event => onInputChange(event, setParams)}
                      />
                      {getText('rawMaterialPrice')}
                    </Flex>
                  ),
                  style: { width: '270px' }
                },
                'nationCurr',
                'unit',
                ...keys.map(({ keyName } = {}) => ({
                  text: keyName,
                  className: 'w-25',
                  childrens: ['code', 'price']
                }))
              ]}
              message={message}
              showLoading={isLoading}
              hover
            >
              {datas
                .slice(0, end)
                .map(
                  ({ pltCode, nationCurr, unit, keyList = [] } = {}, index) => (
                    <tr
                      key={`datas${index}`}
                      className="cursor-pointer"
                      onClick={() => {
                        history.push(ROUTER_PATH.sfms.rawMaterial.price, {
                          factoryCodeSeqList: [params.factoryCodeSeq]
                        });
                      }}
                    >
                      <td>{pltCode}</td>
                      <td>{nationCurr}</td>
                      <td>{unit}</td>
                      {keys.map(({ key } = {}) => {
                        const { rwmtrCd, price } =
                          keyList.find(({ key: k } = {}) => k === key) ?? {};

                        return (
                          <Fragment key={`datas${index}.${key}`}>
                            <td>{rwmtrCd}</td>
                            <td className="text-end">
                              {formatNumber(price, ',0.00')}
                            </td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  )
                )}
            </List>
          </InfiniteScroll>
        </div>
      </Card.Body>
    </Card>
  );
};

RawMaterial.propTypes = {
  // 월
  date: PropTypes.string
};
RawMaterial.defaultProps = {
  date: moment()
    .add(moment().date() >= 22 ? 1 : 0, 'months')
    .format('YYYYMM')
};

export default RawMaterial;
